import React, { useMemo } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { formatCurrency } from '../../qCockpit/components/SharedFrames';
import { normalizeClientAlias } from '../../strategyDesk/clientAlias';
import { shellTokens } from '../../shell/theme/shellTheme';

const TOP_N = 5;

const pnlTone = (value) => {
    if (value > 0) return shellTokens.success;
    if (value < 0) return shellTokens.error;
    return shellTokens.text;
};

const TodayOpenPositions = ({ positions = [], instances = [], loading, onNavigate }) => {
    const instanceByKey = useMemo(
        () =>
            new Map(
                instances.map((instance) => [
                    `${instance.strategyId}:${instance.clientId}`,
                    instance,
                ])
            ),
        [instances]
    );

    const topOpen = useMemo(() => {
        return positions
            .filter((position) => position.status === 'open')
            .sort((a, b) => (a.netPnl ?? 0) - (b.netPnl ?? 0))
            .slice(0, TOP_N);
    }, [positions]);

    if (loading) {
        return (
            <Box>
                {Array.from({ length: TOP_N }).map((_, index) => (
                    <Skeleton
                        key={index}
                        variant="rounded"
                        height={44}
                        sx={{ borderRadius: `${shellTokens.innerRadius}px`, mb: 1 }}
                    />
                ))}
            </Box>
        );
    }

    if (!topOpen.length) {
        return (
            <Typography sx={{ fontSize: '0.82rem', color: shellTokens.muted }}>
                No open positions across the fleet.
            </Typography>
        );
    }

    return (
        <Box>
            {topOpen.map((position) => {
                const instance = instanceByKey.get(`${position.strategyId}:${position.clientId}`);
                const strategyLabel = instance?.strategyLabel || position.strategyId;
                const clientLabel = normalizeClientAlias(instance?.clientAlias, position.clientId);

                return (
                    <Box
                        key={position.positionId}
                        component="button"
                        type="button"
                        onClick={onNavigate}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            px: 1.5,
                            py: 1.25,
                            border: `1px solid ${shellTokens.border}`,
                            borderRadius: `${shellTokens.innerRadius}px`,
                            mb: 1,
                            fontSize: '0.82rem',
                            cursor: 'pointer',
                            bgcolor: shellTokens.surface,
                            fontFamily: 'inherit',
                            textAlign: 'left',
                            '&:hover': {
                                bgcolor: 'rgba(15, 23, 42, 0.03)',
                            },
                        }}
                    >
                        <Typography sx={{ fontSize: 'inherit' }}>
                            <Box component="strong" sx={{ fontWeight: 700 }}>
                                {position.symbol || '—'}
                            </Box>
                            {' · '}
                            {strategyLabel}
                            {' · '}
                            {clientLabel}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: shellTokens.monoFamily,
                                fontWeight: 700,
                                fontSize: 'inherit',
                                color: pnlTone(position.netPnl ?? 0),
                                flexShrink: 0,
                                ml: 1,
                            }}
                        >
                            {formatCurrency(position.netPnl ?? 0)}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

export default TodayOpenPositions;
